import dynamic from 'next/dynamic'
import { useState } from 'react'
import { Schemas } from '~/api/gen'
import { Divider } from '~/components/elements/Divider'
import { FavoriteButtonForPc } from '~/components/elements/FavoriteButtonForPc'
import { AppDownloadDialog } from '~/components/modules/AppDownloadDialogForPc'
import { AppPromotion } from '~/components/modules/AppPromotion'
import { CarouselImages } from '~/components/modules/CarouselImages/CarouselImages'
import { CoordinateListByItemId } from '~/components/modules/CoordinatesList/CoordinateListByItemId/CoordinateListByItemId'
import { CoordinateListUsingSimilarItem } from '~/components/modules/CoordinatesList/CoordinateListUsingSimilarItem/CoordinateListUsingSimilarItem'
import { AppLeadingDialog } from '~/components/modules/HalfModal/AppLeadingDialog'
import { HalfModal } from '~/components/modules/HalfModal/HalfModal'
import { MoreHorizPopup } from '~/components/modules/MoreHorizPopup/MoreHorizPopup'
import { RelatedItemList } from '~/components/modules/RelatedItemList'
import { ShareButtons } from '~/components/modules/ShareButtons'
import { isCategoryAllowedToDisplayAd } from '~/features/shared/ad/lib/isCategoryAllowedToDisplayAd'
import { useIntersection } from '~/hooks/useIntersection'
import { useIsDesktop } from '~/hooks/useMediaQuery'
import { useModal } from '~/hooks/useModal'
import { useSessionStorage } from '~/hooks/useSessionStorage'
import { useSnsShareUrl } from '~/hooks/useSnsShareUrl'
import { ITEM_DETAIL_HALF_MODAL_ADJUST_URL } from '~/lib/constants'
import { createVariantHook } from '~/lib/createVariantHook'
import { NEXT_PUBLIC_ENABLE_INSTREAM_AD } from '~/lib/env'
import { sexToGender } from '~/lib/gender/converter'
import { ScreenIds } from '~/types/screenIds'
import { InstreamAd } from '../shared/ad/components/InstreamAd'
import { getVariant } from '../shared/ad/lib/instreamAdAbTest'
import { Breadcrumb } from '../shared/breadcrumbList/components/Breadcrumb'
import { RelatedItemsForPc } from '../shared/components/RelatedItemsForPc'
import { wearUrl } from '../shared/domain/wearUrl'
import { BrandIntroduction } from './components/BrandIntroduction'
import { ItemInformation } from './components/ItemInformation'
import { PopularBrandsByCategory } from './components/PopularBrandsByCategory'
import { RelatedProductList } from './components/RelatedProductList'
import { spItemDetailAds, pcItemDetailAds } from './data/googleAd'
import { useAffiliateTransition } from './hooks/useAffiliateTransition'
import {
  sendGtmEvAppRedirectHalfModalButton,
  sendGtmEvShowHalfModal,
} from './lib/gtmEvent'

const ThinBannerForSP = dynamic(
  () => import('~/features/shared/banner/SP/ThinBanner'),
  {
    ssr: false,
  }
)

const ThinBannerForPC = dynamic(
  () => import('~/features/shared/banner/PC/ThinBanner'),
  {
    ssr: false,
  }
)

const GoogleAd = dynamic(
  () => import('~/features/shared/ad/components/GoogleAd'),
  {
    ssr: false,
  }
)
export type Props = {
  item: Schemas.item_large
  relatedCoordinateCount: number
  relatedProducts: Schemas.get_product_with_members_response
  relatedItems: Schemas.item_collection_with_purchase_items[]
}

const useInstreamAd = createVariantHook(getVariant)

/* eslint-disable complexity */
export const ItemDetail: React.FC<Props> = ({
  item,
  relatedCoordinateCount,
  relatedProducts,
  relatedItems,
}) => {
  const imageUrls =
    item?.images?.map((image) => image.url_500).filter((url) => url != null) ??
    []

  const displayAd = item.category.child_category.id
    ? isCategoryAllowedToDisplayAd(item.category.child_category.id)
    : true // 子カテゴリーが設定されてなければ広告表示

  useAffiliateTransition(item.id)

  const { twitterShareUrl, facebookShareUrl } = useSnsShareUrl({
    shareUrl: wearUrl(`/item/${item.id}/`).href,
    utmCampaign: `ItemID_${item.id}`,
    hashtags: ['WEARコーデ'],
  })

  const [
    AppDownloadModal,
    openAppDownloadModal,
    closeAppDownloadModal,
    isAppDownloadModalOpen,
  ] = useModal(200)

  const isDesktop = useIsDesktop()
  const [isHalfModalOpen, setIsHalfModalOpen] = useState(false)
  const {
    isReady: isSessionStorageReady,
    value: halfModalShowed,
    set: setHalfModalShowed,
  } = useSessionStorage('showed_app_redirect_half_modal_on_item_detail')
  const [halfModalMarkerRef, intersecting] = useIntersection()
  const closeHalfModal = () => setIsHalfModalOpen(false)
  if (
    intersecting &&
    isSessionStorageReady &&
    !halfModalShowed &&
    !isHalfModalOpen
  ) {
    sendGtmEvShowHalfModal()
    setIsHalfModalOpen(true)
    setHalfModalShowed(true)
  }

  const displayInstreamAd =
    useInstreamAd() === 'on' &&
    NEXT_PUBLIC_ENABLE_INSTREAM_AD === 'true' &&
    isDesktop

  return (
    <main className="xl:bg-gray-50">
      <div className="xl:hidden">
        <ThinBannerForSP bannerType="sp_narrow_menu_item_detail" />
      </div>
      <div className="hidden xl:block">
        <ThinBannerForPC bannerType="pc_gbl_ad_item_detail" />
      </div>
      {displayAd && (
        <section className="bg-gray-200 xl:hidden">
          <GoogleAd elementId={spItemDetailAds.top} />
        </section>
      )}
      {relatedCoordinateCount > 0 && (
        <section className="bg-gray-50 py-[10px] xl:hidden">
          <p className="text-center text-[11px] leading-[1.1]">
            このアイテムには
            <a
              href={`/coordinate/?item_id=${item.id}`}
              className="font-bold text-blue-500"
            >
              {relatedCoordinateCount}件の着用コーディネート
            </a>
            があります
          </p>
        </section>
      )}
      <div className="hidden xl:mx-auto xl:flex xl:w-[990px] xl:items-center xl:justify-between xl:pt-[22px]">
        <Breadcrumb />
        <MoreHorizPopup>
          <div className="py-[7px]">
            <ShareButtons
              twitterShareUrl={twitterShareUrl}
              facebookShareUrl={facebookShareUrl}
            />
          </div>
        </MoreHorizPopup>
      </div>
      <div className="hidden xl:flex xl:justify-center xl:pb-[30px] xl:pt-[15px]">
        <GoogleAd elementId={pcItemDetailAds.top} />
      </div>
      <div className="xl:mx-auto xl:grid xl:w-[990px] xl:grid-cols-[558px_1fr] xl:grid-rows-[max-content_1fr] xl:gap-x-5 xl:pt-4 xl:[grid-template-areas:'main_sub'_'main-bottom_sub']">
        <div className="xl:[grid-area:main]">
          <section className="relative border-b border-gray-300 xl:overflow-hidden xl:rounded-[3px] xl:border xl:bg-white">
            <CarouselImages imageUrls={imageUrls} alt={item.name} />
            <div className="hidden xl:block">
              <Divider />
              <div className="flex justify-between px-[23px] py-6">
                <FavoriteButtonForPc
                  favoriteCount={item.aggregate?.folder_count ?? 0}
                  onButtonClick={openAppDownloadModal}
                />
              </div>
            </div>
          </section>
        </div>
        <div className="pt-[11px] xl:pt-0 xl:[grid-area:sub]">
          <ItemInformation item={item} />
          <div ref={halfModalMarkerRef} />
          {relatedItems.length > 0 && item.id !== null && (
            <div className="hidden pt-[18px] xl:block">
              <RelatedItemsForPc
                title="関連アイテム"
                items={relatedItems}
                gender={sexToGender(item.sex_id)}
                affiliate={{
                  screenId: ScreenIds.ItemDetail,
                  objectId: item.id,
                }}
              />
            </div>
          )}
          {item.brand.id && (
            <div className="hidden pt-[18px] xl:block">
              <BrandIntroduction
                brand={{
                  id: item.brand.id,
                  name: item.brand.name,
                  name_kana: item.brand.name_kana,
                  concept: item.brand.concept,
                  url: item.brand.url ?? undefined,
                }}
              />
            </div>
          )}
          <div className="hidden xl:flex xl:justify-center xl:pt-10">
            {displayInstreamAd ? (
              <InstreamAd className="w-[320px]" />
            ) : (
              <GoogleAd elementId={pcItemDetailAds.sub} />
            )}
          </div>
        </div>
        <div className="xl:[grid-area:main-bottom]">
          <section className="pb-2 pt-6 empty:hidden xl:hidden">
            <RelatedProductList relatedProducts={relatedProducts} />
          </section>
          {item.id !== null && (
            <section className="-mb-2 pt-[10px] empty:hidden xl:mt-0 xl:pt-[18px]">
              <CoordinateListByItemId itemId={item.id} />
            </section>
          )}
          <section className="pt-[10px] empty:hidden xl:pt-2">
            <CoordinateListUsingSimilarItem
              brandId={item.brand.id}
              brandName={item.brand.name}
              categoryId={item.category.id}
              childCategoryId={item.category.child_category.id}
              pageGroup="content_f_itemdetail"
            />
          </section>
          <section className="xl:hidden">
            <AppPromotion pageGroup="content_f_itemdetail" />
          </section>
          {relatedItems.length > 0 && (
            <section className="mb-[-18px] pt-[11px] xl:hidden">
              <RelatedItemList
                items={relatedItems}
                gender={sexToGender(item.sex_id)}
              />
            </section>
          )}
          {item.category.id !== null && item.category.keyword && (
            <div className="px-5 pt-6 xl:px-0 xl:pt-[27px]">
              <PopularBrandsByCategory
                gender={sexToGender(item.sex_id)}
                category={{
                  id: item.category.id,
                  name: item.category.name,
                  key: item.category.keyword,
                }}
                childCategory={
                  item.category.child_category.id !== null &&
                  item.category.child_category.keyword
                    ? {
                        id: item.category.child_category.id,
                        name: item.category.child_category.name,
                        key: item.category.child_category.keyword,
                      }
                    : undefined
                }
              />
            </div>
          )}
          {displayAd && (
            <section className="pt-[30px] xl:hidden">
              <GoogleAd elementId={spItemDetailAds.footer} />
            </section>
          )}
        </div>
      </div>
      <AppDownloadModal
        wrapperClassName={`ease-linear duration-200 transition-all ${
          isAppDownloadModalOpen ? 'opacity-100' : 'opacity-0'
        }`}
        overlayClassName="bg-black/60"
      >
        <AppDownloadDialog
          close={closeAppDownloadModal}
          text={'「お気に入り」をするには\nアプリが必要です'}
          imageType="itemDetail"
        />
      </AppDownloadModal>
      <HalfModal
        isOpen={isHalfModalOpen && !isDesktop}
        onClose={closeHalfModal}
      >
        <AppLeadingDialog
          onButtonClick={sendGtmEvAppRedirectHalfModalButton}
          onCloseClick={closeHalfModal}
          title={'アプリで気になるアイテムの\n着回しをサクサク見よう'}
          message={
            'アプリならコーディネートを保存して\nいつでも見返すことができます。'
          }
          buttonText="アプリで開く"
          href={ITEM_DETAIL_HALF_MODAL_ADJUST_URL}
          target="_blank"
          rel="noreferrer"
        />
      </HalfModal>
    </main>
  )
}
